import {
  faCheckCircle,
  faClipboardCheck,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useContext, useState } from 'react';
import { isInspectionSigned } from '../../../../contexts/InspectionContext';
import { ProjectAsset, projectAssetsApi } from '../../../../utils/api/projectAsset';
import AssetChecksModal from '../../../AssetTypes/AssetCard/AssetChecksModal';
import {
  archivedOnLabel,
  inspectableFieldsOf,
  unansweredChecks,
} from '../../../AssetTypes/AssetCard/AssetChecks';
import { MapEditorRouteContext } from '../../../CustomRoutes/MapEditor';
import { Context as DataSourceContext } from '../../DataSourceContext';

const coord = (value?: number) =>
  value == null ? null : Number(value).toFixed(7);

/**
 * Card shown when an asset marker on the site map is clicked: identity, check
 * status and location, with a link into the inspection checks modal. Uses the
 * legend card shell so it matches the other map cards.
 */
const AssetMapCard: React.FC<{
  /** `feature.values_` of the clicked marker. */
  feature: { positionable_source_id?: number };
  onClose: () => void;
}> = ({ feature, onClose }) => {
  const dataSourceContext = useContext(DataSourceContext);
  const { projectId, selectedInspection } = useContext(MapEditorRouteContext);
  const [showChecks, setShowChecks] = useState(false);

  const asset: ProjectAsset | null = dataSourceContext.getById(
    feature.positionable_source_id,
    'asset_type',
  );

  if (!asset) return null;

  const inspectionId = selectedInspection?.id;
  const archivedOn = archivedOnLabel(asset);
  const canEdit =
    !archivedOn &&
    !dataSourceContext.isReadOnlyDataType('asset_type') &&
    !!selectedInspection &&
    !isInspectionSigned(selectedInspection);
  const checksComplete = unansweredChecks(asset).length === 0;
  const showChecksBadge = !!inspectionId && !!inspectableFieldsOf(asset).length;
  const latLng = [coord(asset.latitude), coord(asset.longitude)]
    .filter(Boolean)
    .join(', ');

  // Keep the map's copy of the asset current as the modal saves each answer.
  const refresh = () =>
    projectAssetsApi
      .show(projectId, asset.id, inspectionId)
      .then((fresh) =>
        dataSourceContext.updateArrayItem('asset_type', {
          ...fresh,
          positionableType: 'asset_type',
        }),
      )
      .catch(() => {});

  // The checks modal replaces the card while it is open.
  if (showChecks && inspectionId) {
    return (
      <AssetChecksModal
        projectId={projectId}
        inspectionId={inspectionId}
        asset={asset}
        canEdit={canEdit}
        onChanged={refresh}
        onClose={() => setShowChecks(false)}
      />
    );
  }

  return (
    <div className='LegendCardContent asset-map-card'>
      <header>
        <span>
          {asset.asset_type?.code}-{asset.asset_name}
        </span>
        <span onClick={onClose}>CLOSE</span>
      </header>

      {asset.asset_type?.name && (
        <p className='asset-map-card__type'>{asset.asset_type.name}</p>
      )}

      {archivedOn && (
        <p className='asset-map-card__badge is-archived'>{archivedOn}</p>
      )}

      {showChecksBadge && (
        <p
          className={classNames('asset-map-card__badge', {
            'is-complete': checksComplete,
          })}
        >
          <FontAwesomeIcon
            icon={checksComplete ? faCheckCircle : faExclamationTriangle}
          />{' '}
          {checksComplete ? 'Complete' : 'Pending'}
        </p>
      )}

      <dl className='asset-map-card__rows'>
        <div>
          <dt>Status</dt>
          <dd>{asset.asset_status?.name || '—'}</dd>
        </div>
        <div>
          <dt>City / State</dt>
          <dd>{[asset.city, asset.state].filter(Boolean).join(' , ') || '—'}</dd>
        </div>
        <div>
          <dt>County</dt>
          <dd>{asset.county || '—'}</dd>
        </div>
        <div>
          <dt>Lat / Lng</dt>
          <dd>{latLng || '—'}</dd>
        </div>
      </dl>

      {!!inspectionId && (
        <button
          type='button'
          className='primary asset-map-card__checks'
          onClick={() => setShowChecks(true)}
        >
          <FontAwesomeIcon icon={faClipboardCheck} />{' '}
          {checksComplete ? 'Review' : 'Complete'} Inspection Checks
        </button>
      )}
    </div>
  );
};

export default AssetMapCard;
